import { z } from 'zod'

/**
 * Field validation presets — Part Three D7, register §8.
 *
 * The preset list is closed and versioned: `None | Email | Numbers | Letters | Date |
 * ZIP | ZIP+4 | SSN | Custom`. `SSN` is format-only — never identity verification,
 * never sent to telemetry or a validation service, and never leaves the device. `ZIP`
 * preserves leading zeros. Optional fields may stay empty; if filled they must satisfy
 * their configured validation.
 *
 * `Custom` is backed by a bounded safe pattern engine: a pattern is declared data, is
 * validated against a conservative safe subset, and is never compiled or executed by
 * these contracts. The maximum accepted value length is a register value
 * (`custom_pattern_max_length`, `V17-CFG-11`); it is deliberately unset until the
 * profile freeze in register §10 is approved, so callers pass it in and fail closed.
 */

export const VALIDATION_PROFILE_VERSION = 1 as const

export const ValidationPreset = z.enum([
  'None',
  'Email',
  'Numbers',
  'Letters',
  'Date',
  'ZIP',
  'ZIP+4',
  'SSN',
  'Custom',
])
export type ValidationPreset = z.infer<typeof ValidationPreset>

/** The nine adopted presets, in register §8 order. */
export const VALIDATION_PRESETS = ValidationPreset.options

export const ValidationValueShape = z.enum([
  'none',
  'email',
  'decimal',
  'integer',
  'unicode_letters_marks',
  'calendar_date',
  'zip5',
  'zip9',
  'ssn_format',
])
export type ValidationValueShape = z.infer<typeof ValidationValueShape>

export interface ValidationPresetRule {
  readonly preset: ValidationPreset
  readonly value_shape: ValidationValueShape
  /** Format conformance only — never identity verification (D7). */
  readonly format_only: boolean
  /** True when the raw value must never leave the device or reach telemetry. */
  readonly never_leaves_device: boolean
  /** ZIP/ZIP+4 keep leading zeros: the value is textual, not numeric. */
  readonly preserves_leading_zeros: boolean
  readonly requires_custom_pattern: boolean
}

/** Per-preset rules from D7 / register §8. Complete by construction (Record). */
export const VALIDATION_PRESET_RULES = {
  None: {
    preset: 'None',
    value_shape: 'none',
    format_only: false,
    never_leaves_device: false,
    preserves_leading_zeros: false,
    requires_custom_pattern: false,
  },
  Email: {
    preset: 'Email',
    value_shape: 'email',
    format_only: false,
    never_leaves_device: false,
    preserves_leading_zeros: false,
    requires_custom_pattern: false,
  },
  Numbers: {
    preset: 'Numbers',
    value_shape: 'decimal',
    format_only: false,
    never_leaves_device: false,
    preserves_leading_zeros: false,
    requires_custom_pattern: false,
  },
  Letters: {
    preset: 'Letters',
    value_shape: 'unicode_letters_marks',
    format_only: false,
    never_leaves_device: false,
    preserves_leading_zeros: false,
    requires_custom_pattern: false,
  },
  Date: {
    preset: 'Date',
    value_shape: 'calendar_date',
    format_only: false,
    never_leaves_device: false,
    preserves_leading_zeros: false,
    requires_custom_pattern: false,
  },
  ZIP: {
    preset: 'ZIP',
    value_shape: 'zip5',
    format_only: false,
    never_leaves_device: false,
    preserves_leading_zeros: true,
    requires_custom_pattern: false,
  },
  'ZIP+4': {
    preset: 'ZIP+4',
    value_shape: 'zip9',
    format_only: false,
    never_leaves_device: false,
    preserves_leading_zeros: true,
    requires_custom_pattern: false,
  },
  SSN: {
    preset: 'SSN',
    value_shape: 'ssn_format',
    format_only: true,
    never_leaves_device: true,
    preserves_leading_zeros: true,
    requires_custom_pattern: false,
  },
  Custom: {
    preset: 'Custom',
    value_shape: 'none',
    format_only: false,
    never_leaves_device: false,
    preserves_leading_zeros: false,
    requires_custom_pattern: true,
  },
} as const satisfies Readonly<Record<ValidationPreset, ValidationPresetRule>>

export type CustomPatternSafetyCode =
  'UNBOUNDED_QUANTIFIER' | 'BACKREFERENCE' | 'GROUP_EXTENSION' | 'UNICODE_PROPERTY'

export type CustomPatternViolationCode = CustomPatternSafetyCode | 'BOUND_EXCEEDED'

export interface CustomPatternCheck {
  readonly ok: boolean
  readonly violations: readonly CustomPatternViolationCode[]
}

export interface CustomPatternSafetyResult {
  readonly ok: boolean
  readonly code?: CustomPatternSafetyCode
}

/**
 * Conservative safety check for the bounded custom-pattern engine (D7).
 * Rejects constructs that permit executable code, backtracking blow-ups or unbounded
 * matching: `*`, `+`, `{n,}`, backreferences, `(?…)` group extensions
 * (lookaround/atomic/inline flags/non-capturing) and `\p{…}` property escapes. The
 * pattern is inspected as text and is never compiled or executed here.
 */
export function checkCustomPatternSafety(pattern: string): CustomPatternSafetyResult {
  if (/[*+]|\{\d+,\}/.test(pattern)) return { ok: false, code: 'UNBOUNDED_QUANTIFIER' }
  if (/\\[1-9]/.test(pattern)) return { ok: false, code: 'BACKREFERENCE' }
  if (/\(\?/.test(pattern)) return { ok: false, code: 'GROUP_EXTENSION' }
  if (/\\[pP]\{/.test(pattern)) return { ok: false, code: 'UNICODE_PROPERTY' }
  return { ok: true }
}

/**
 * Bounded custom pattern — declared data only. `max_length` is the maximum accepted
 * value length in Unicode scalar values and is bounded by the register value
 * `custom_pattern_max_length` (checked by `validateCustomPattern`).
 */
export const CustomPattern = z
  .object({
    pattern: z.string().min(1),
    max_length: z.number().int().positive(),
    profile_version: z.literal(VALIDATION_PROFILE_VERSION),
  })
  .strict()
  .superRefine((value, ctx) => {
    const safety = checkCustomPatternSafety(value.pattern)
    if (!safety.ok) {
      ctx.addIssue({
        code: 'custom',
        message: `custom pattern rejected: ${safety.code ?? 'UNSAFE'}`,
        path: ['pattern'],
      })
    }
  })
export type CustomPattern = z.infer<typeof CustomPattern>

/** Check a custom pattern against the register's bounded-pattern budget. */
export function validateCustomPattern(
  pattern: CustomPattern,
  registerMaxLength: number,
): CustomPatternCheck {
  const violations: CustomPatternViolationCode[] = []
  const safety = checkCustomPatternSafety(pattern.pattern)
  if (!safety.ok && safety.code !== undefined) violations.push(safety.code)
  if (pattern.max_length > registerMaxLength) violations.push('BOUND_EXCEEDED')
  return { ok: violations.length === 0, violations }
}

/**
 * The D7 validation attribute of a field. `None` means no constraint; `Custom`
 * requires exactly one bounded pattern; a sender may lower the text limit but the
 * register maximum is applied by the caller.
 */
export const FieldValidation = z
  .object({
    preset: ValidationPreset,
    profile_version: z.literal(VALIDATION_PROFILE_VERSION),
    custom_pattern: CustomPattern.optional(),
    text_max_scalars: z.number().int().positive().optional(),
  })
  .strict()
  .superRefine((validation, ctx) => {
    if (validation.preset === 'Custom' && validation.custom_pattern === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'the Custom preset requires a bounded pattern',
        path: ['custom_pattern'],
      })
    }
    if (validation.preset !== 'Custom' && validation.custom_pattern !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'custom_pattern is only valid with the Custom preset',
        path: ['custom_pattern'],
      })
    }
  })
export type FieldValidation = z.infer<typeof FieldValidation>
